
import { Component, Mixins, Prop } from 'vue-property-decorator';
import { OrderApi, OrderCalculateProducts } from '@/api';
import { OrderItem, SapProduct } from '@/api/model';
import OrderTotals from '@/components/orders/OrderTotals.vue';
import MessageToastMixin from '@/mixins/MessageToastMixin';
import { SalesOrderAll as SalesOrder } from '@/logic/orders/SalesOrderAll';
import getConfiguration from '@/store/getConfiguration';
import NewOrderFooterMessage from '@/components/orders/new-order/NewOrderFooterMessage.vue';

@Component({
  components: { NewOrderFooterMessage, OrderTotals },
})
export default class NewOrderFooter extends Mixins(MessageToastMixin) {
  @Prop(String)
  step!: string;

  @Prop(Boolean)
  isInvalid!: boolean;

  loading = false;

  steps: string[] = [
    'select-products',
    'order-reference-and-shipping',
    'order-summary-and-confirmation',
  ];

  get cart(): SalesOrder {
    return this.$store.state.order.cart;
  }

  get products(): SapProduct[] {
    return this.$store.state.order.products;
  }

  get items(): OrderItem[] {
    return this.cart.OrderItems || [];
  }

  get relatedProducts(): SapProduct[] {
    if (!this.products) {
      return [];
    }
    return this.products.filter((product: SapProduct) =>
      this.items.some(
        (item: OrderItem) => item.ProductNr === product.ProductNr,
      ),
    );
  }

  // Check allowed
  get canGoNext(): boolean {
    const step1 = this.items.length > 0;
    const step2 =
      !!this.cart.ShipToParty && !!this.cart.ReqDlvDate && !this.isInvalid;
    const step3 = step1 && step2;
    const allowed: any = {
      'select-products': step1,
      'order-reference-and-shipping': step2,
      'order-summary-and-confirmation': step3,
    };
    return allowed[this.step];
  }

  get isSticky(): boolean {
    return this.step === 'select-products';
  }

  get showTotals(): boolean {
    return this.step === 'select-products';
  }

  get nextLabel(): string {
    if (this.step === 'order-summary-and-confirmation') {
      return 'confirm';
    }
    if (
      this.step === 'select-products' &&
      (this.action === 'suggest-remove' ||
        this.action === 'overfill-no-suggestion')
    ) {
      return 'recalculate';
    }
    return 'next';
  }

  get previousLabel(): string {
    return this.step === this.steps[0] ? 'cancel' : 'back';
  }

  get action(): string {
    return this.$store.getters['order/suggestionAction'];
  }

  goNext() {
    if (!this.canGoNext) {
      return;
    }
    switch (this.step) {
      case 'select-products':
        this.goNextSelectProducts();
        break;
      case 'order-reference-and-shipping':
        this.$router.push({
          name: 'new-order-steps',
          params: { step: 'order-summary-and-confirmation' },
        });
        break;
      case 'order-summary-and-confirmation':
        this.confirmOrder();
        break;
      default:
        break;
    }
  }

  // Check the truck optimizer
  async goNextSelectProducts() {
    if (this.$store.state.order.cartChanged) {
      this.$store.dispatch('order/resetSuggestion');
    }
    const initialAction = this.action;

    await this.calculateTruck();
    if (
      this.action !== 'suggest-remove' &&
      this.action !== 'overfill-no-suggestion'
    ) {
      if (this.action === 'next-step' || initialAction === this.action) {
        this.goToReferenceAndShipping();
      }
    }
  }

  goToReferenceAndShipping() {
    this.$router.push({
      name: 'new-order-steps',
      params: { step: 'order-reference-and-shipping' },
    });
  }

  goPrevious() {
    switch (this.step) {
      case 'select-products':
        this.$store.commit('order/resetCart');
        this.$router.push({ name: 'orders' });
        break;
      case 'order-reference-and-shipping':
        this.$router.push({
          name: 'new-order-steps',
          params: { step: 'select-products' },
        });
        break;
      case 'order-summary-and-confirmation':
        this.$router.push({
          name: 'new-order-steps',
          params: { step: 'order-reference-and-shipping' },
        });
        break;
      default:
        break;
    }
  }

  async confirmOrder() {
    try {
      this.loading = true;
      await this.$store.dispatch('order/postNewOrder', this.cart);
      this.loading = false;
      this.$router.push({ name: 'orders' });
      this.$buefy.dialog.alert({
        title: this.$t('orders.new.confirm-dialog.title') as string,
        message: this.$t('orders.new.confirm-dialog.message') as string,
        confirmText: this.$t('orders.new.confirm-dialog.button') as string,
      });
    } catch (e) {
      this.showError(this.$t('general.messages.error'));
      this.loading = false;
    }
  }

  async getApi() {
    const configuration = await getConfiguration();
    return new OrderApi(configuration, '');
  }

  async calculateTruck(): Promise<any> {
    try {
      this.loading = true;
      this.$store.commit('order/setSuggestion', {});
      const products = this.getCalculationProducts(this.items);
      const api = await this.getApi();
      const { data: result } = await api.orderCalculatePost({
        Products: products,
      });
      this.$store.commit('order/setSuggestion', result);
    } catch (e) {
      // If the calculator doesn't work for some reason, go to the next step in order process
    } finally {
      this.loading = false;
    }
  }

  getCalculationProducts(items: OrderItem[]): OrderCalculateProducts[] {
    // Convert shopping cart items to Products for calculation
    const products: OrderCalculateProducts[] = items.map((i: OrderItem) => {
      const quantity = i.Quantity ? parseInt(i.Quantity, 10) : 0;
      return {
        NumPallets: i.SalesUnit === 'PAL' && quantity ? quantity : 0,
        NumLayers: i.SalesUnit === 'LAY' && quantity ? quantity : 0,
        ProductNr: i.ProductNr,
      } as OrderCalculateProducts;
    });

    // Combine layers and pallets items
    const combined: OrderCalculateProducts[] = products.reduce(
      (acc: OrderCalculateProducts[], cur: OrderCalculateProducts) => {
        const product: OrderCalculateProducts | undefined = acc.find(
          (p) => p.ProductNr === cur.ProductNr,
        );
        if (product === undefined) {
          acc.push(cur);
        } else {
          product.NumLayers += cur.NumLayers;
          product.NumPallets += cur.NumPallets;
        }
        return acc;
      },
      [],
    );
    return combined;
  }
}
